import { 
  POWER_DISPLAY_DECIMALS,
  POWER_THRESHOLD_PERCENT,
  GIFTS_THRESHOLD
} from './constants';

/**
 * Format power value for display in billions with 3 decimal places
 * @param {number} power - The raw power value
 * @returns {string} Formatted power string
 */
export const formatPowerDisplay = (power) => {
  return (power || 0).toFixed(POWER_DISPLAY_DECIMALS);
};

/**
 * Create duels from a ranking array by pairing consecutive teams
 * @param {Array} ranking - Array of teams (can contain nulls)
 * @param {boolean} allowPartial - Whether to allow duels with one or both null teams
 * @returns {Array} Array of duel pairs
 */
export const createDuels = (ranking, allowPartial = false) => {
  const duels = [];
  for (let i = 0; i < ranking.length; i += 2) {
    const team1 = ranking[i];
    const team2 = ranking[i + 1];
    
    if (allowPartial || (team1 && team2)) {
      duels.push([team1 || null, team2 || null]);
    }
  }
  return duels;
};

/**
 * Determine the winner of a duel based on power and gifts
 * @param {Object} team1 - First team
 * @param {Object} team2 - Second team
 * @returns {number} ID of the winning team
 */
export const determineWinner = (team1, team2) => {
  const power1 = team1.power || 0;
  const power2 = team2.power || 0;
  const gifts1 = team1.gifts || 0;
  const gifts2 = team2.gifts || 0;

  // Calculate power difference percentage
  const maxPower = Math.max(power1, power2);
  const powerDiff = Math.abs(power1 - power2);
  const powerDiffPercent = maxPower > 0 ? (powerDiff / maxPower) * 100 : 0;

  if (powerDiffPercent <= POWER_THRESHOLD_PERCENT) {
    // Powers within threshold, check gifts
    const giftDiff = Math.abs(gifts1 - gifts2);

    if (giftDiff <= GIFTS_THRESHOLD) {
      // Gifts within threshold, choose randomly
      return Math.random() < 0.5 ? team1.id : team2.id;
    } else {
      // Choose based on higher gifts
      return gifts1 > gifts2 ? team1.id : team2.id;
    }
  } else {
    // Choose based on higher power
    return power1 > power2 ? team1.id : team2.id;
  }
};

/**
 * Calculate the next round ranking based on current winners
 * @param {Array} duels - Array of duel pairs
 * @param {Object} currentWinners - Map of duel index to winner ID
 * @param {boolean} keepNulls - Whether to keep null placeholders for incomplete duels
 * @returns {Array} Combined ranking for next round (winners first, then losers)
 */
export const calculateNextRanking = (duels, currentWinners, keepNulls = false) => {
  const nextRanking = [];
  const losers = [];

  duels.forEach((duel, idx) => {
    const winnerId = currentWinners[idx];
    if (winnerId !== undefined) {
      const winner = duel.find(t => t && t.id === winnerId);
      const loser = duel.find(t => t && t.id !== winnerId);

      if (winner && loser) {
        nextRanking.push(winner);
        losers.push(loser);
      }
    } else {
      nextRanking.push(null);
      losers.push(null);
    }
  });

  const combined = [...nextRanking, ...losers];
  return keepNulls ? combined : combined.filter(t => t !== null);
};

/**
 * Calculate win statistics for teams up to a given round
 * @param {Array} allRounds - Array of all rounds
 * @param {Array} allWinners - Array of winners for each round
 * @param {number} upToRound - Calculate stats up to this round (inclusive)
 * @returns {Map} Map of team ID to stats { wins, winRounds }
 */
const calculateWinStats = (allRounds, allWinners, upToRound) => {
  const teamStats = new Map();

  // Initialize all teams from round 0
  if (allRounds[0]) {
    allRounds[0].forEach(team => {
      if (team) {
        teamStats.set(team.id, {
          wins: 0,
          winRounds: []
        });
      }
    });
  }

  // Calculate wins for each round up to upToRound
  for (let roundIndex = 0; roundIndex <= upToRound; roundIndex++) {
    const roundRanking = allRounds[roundIndex];
    if (!roundRanking || roundRanking.length === 0) continue;

    const roundDuels = createDuels(roundRanking);
    const roundWinners = allWinners[roundIndex] || {};

    roundDuels.forEach((duel, duelIndex) => {
      const winnerId = roundWinners[duelIndex];
      if (winnerId !== undefined && duel[0] && duel[1]) {
        const winner = teamStats.get(winnerId);
        if (winner) {
          winner.wins++;
          winner.winRounds.push(roundIndex);
        }
      }
    });
  }

  return teamStats;
};

/**
 * Calculate the next round ranking with sorting based on win statistics
 * @param {Array} duels - Array of duel pairs from current round
 * @param {Object} currentWinners - Map of duel index to winner ID for current round
 * @param {Array} previousRoundRanking - Ranking from the previous round (for tie-breaking)
 * @param {Array} allRounds - Array of all rounds
 * @param {Array} allWinners - Array of winners for each round
 * @param {number} currentRoundIndex - Index of the current round (0-based)
 * @param {boolean} keepNulls - Whether to keep null placeholders for incomplete duels
 * @returns {Array} Sorted ranking for next round
 */
export const calculateNextRankingWithSorting = (
  duels,
  currentWinners,
  previousRoundRanking,
  allRounds,
  allWinners,
  currentRoundIndex,
  keepNulls = false
) => {
  // Collect all teams from current round (winners and losers)
  const allTeams = [];
  
  duels.forEach((duel, idx) => {
    const winnerId = currentWinners[idx];
    if (winnerId !== undefined) {
      const winner = duel.find(t => t && t.id === winnerId);
      const loser = duel.find(t => t && t.id !== winnerId);
      
      if (winner) allTeams.push(winner);
      if (loser) allTeams.push(loser);
    } else if (keepNulls) {
      allTeams.push(null);
      allTeams.push(null);
    }
  });

  // If we have nulls and keepNulls is false, filter them out
  const teamsToSort = keepNulls ? allTeams : allTeams.filter(t => t !== null);
  
  // Calculate win statistics up to current round
  const winStats = calculateWinStats(allRounds, allWinners, currentRoundIndex);

  // Create a map of previous round positions for tie-breaking
  const previousPositions = new Map();
  if (previousRoundRanking) {
    previousRoundRanking.forEach((team, index) => {
      if (team) {
        previousPositions.set(team.id, index);
      }
    });
  }

  // Sort teams according to rules:
  // 1. Higher number of victories leads to higher ranking
  // 2. If victory counts are equal, earlier wins rank higher
  // 3. When wins and order of wins are the same, maintain previous round order
  teamsToSort.sort((a, b) => {
    // Handle nulls
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    const statsA = winStats.get(a.id) || { wins: 0, winRounds: [] };
    const statsB = winStats.get(b.id) || { wins: 0, winRounds: [] };

    // Rule 1: Compare by number of wins (descending)
    if (statsB.wins !== statsA.wins) {
      return statsB.wins - statsA.wins;
    }

    // Rule 2: If wins are equal, compare by earliest win round
    if (statsA.winRounds.length > 0 && statsB.winRounds.length > 0) {
      const aEarliestWin = Math.min(...statsA.winRounds);
      const bEarliestWin = Math.min(...statsB.winRounds);
      if (aEarliestWin !== bEarliestWin) {
        return aEarliestWin - bEarliestWin; // Earlier round = higher rank
      }

      // If earliest wins are in the same round, compare all win rounds in order
      for (let i = 0; i < Math.min(statsA.winRounds.length, statsB.winRounds.length); i++) {
        if (statsA.winRounds[i] !== statsB.winRounds[i]) {
          return statsA.winRounds[i] - statsB.winRounds[i]; // Earlier round = higher rank
        }
      }
    }

    // Rule 3: If wins and order of wins are the same, maintain previous round order
    const posA = previousPositions.get(a.id);
    const posB = previousPositions.get(b.id);
    
    if (posA !== undefined && posB !== undefined) {
      return posA - posB; // Maintain previous order
    }
    
    // If one team wasn't in previous round, put it after the one that was
    if (posA !== undefined) return -1;
    if (posB !== undefined) return 1;
    
    // If neither was in previous round, maintain current order
    return 0;
  });

  return teamsToSort;
};

/**
 * Update a specific property of a team across all rounds
 * @param {Array} rounds - Array of all rounds
 * @param {number} teamId - ID of the team to update
 * @param {string} property - Property name to update
 * @param {any} value - New value for the property
 * @returns {Array} Updated rounds array
 */
export const updateTeamInAllRounds = (rounds, teamId, property, value) => {
  return rounds.map(round => 
    round.map(t => t && t.id === teamId ? { ...t, [property]: value } : t)
  );
};

/**
 * Serialize game state to compact base64 format
 * Only stores team names in round 0, references by ID in later rounds
 * @param {Array} rounds - Array of all rounds
 * @param {Array} winners - Array of winner objects for each round
 * @param {number|null} selectedTeamId - Currently selected team ID
 * @param {Array} lockedRounds - Array of booleans indicating which rounds are locked
 * @returns {string} URL-safe base64 encoded state string
 */
export const serializeGameState = (rounds, winners, selectedTeamId, lockedRounds = [true, false, false, false]) => {
  // Create a name lookup map from round 0
  const nameMap = new Map();
  if (rounds[0]) {
    rounds[0].forEach(team => {
      if (team) {
        nameMap.set(team.id, team.name);
      }
    });
  }

  // Serialize rounds: round 0 with names, power, and gifts; rounds 1-3 only IDs
  // Power and gifts don't change, so only store them in round 0
  const serializedRounds = rounds.map((round, roundIndex) => {
    if (!round || round.length === 0) return [];
    return round.map(team => {
      if (!team) return null;
      if (roundIndex === 0) {
        // Round 0: include name, power, and gifts
        return { i: team.id, n: team.name, p: team.power || 0, g: team.gifts || 0 };
      } else {
        // Rounds 1-3: only ID (power and gifts are the same as round 0)
        return team.id;
      }
    });
  });

  // Serialize winners (already compact objects)
  const serializedWinners = winners.map(winnerObj => {
    const result = {};
    Object.keys(winnerObj).forEach(key => {
      result[key] = winnerObj[key];
    });
    return result;
  });

  // Calculate number of locked rounds (0-4)
  const lockedRoundsCount = Array.isArray(lockedRounds) 
    ? lockedRounds.filter(locked => locked === true).length 
    : 0;

  // Create compact state object
  const state = {
    r: serializedRounds,
    w: serializedWinners,
    s: selectedTeamId,
    l: lockedRoundsCount // Number of locked rounds [0-4]
  };

  // Convert to compact JSON (no spaces)
  const jsonString = JSON.stringify(state);

  // Encode to base64 with URL-safe encoding
  const base64 = btoa(jsonString)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return base64;
};

/**
 * Deserialize game state from base64 format
 * Reconstructs team names for rounds 1-3 from round 0
 * @param {string} base64String - URL-safe base64 encoded state string
 * @returns {Object} Object with rounds, winners, selectedTeamId, and lockedRounds
 */
export const deserializeGameState = (base64String) => {
  try {
    if (!base64String || typeof base64String !== 'string') {
      throw new Error('Invalid base64 string');
    }

    // Decode URL-safe base64
    let base64 = base64String
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // Add padding if needed
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    // Decode base64
    const jsonString = atob(base64);

    // Parse JSON
    const state = JSON.parse(jsonString);

    // Reconstruct rounds - ensure we always have 4 rounds
    const rounds = [[], [], [], []];
    const nameMap = new Map();

    // Create power/gifts lookup map from round 0
    const powerMap = new Map();
    const giftsMap = new Map();

    // First pass: deserialize round 0 and build lookup maps
    if (state.r && Array.isArray(state.r) && state.r[0]) {
      rounds[0] = state.r[0].map(teamData => {
        if (!teamData) return null;
        const team = {
          id: teamData.i,
          name: teamData.n || '',
          power: teamData.p || 0,
          gifts: teamData.g || 0
        };
        nameMap.set(team.id, team.name);
        powerMap.set(team.id, team.power);
        giftsMap.set(team.id, team.gifts);
        return team;
      });
    }

    // Second pass: deserialize rounds 1-3 and look up names, power, gifts from round 0
    for (let i = 1; i < 4; i++) {
      if (state.r && Array.isArray(state.r) && state.r[i] && Array.isArray(state.r[i])) {
        rounds[i] = state.r[i].map(teamData => {
          if (teamData === null || teamData === undefined) return null;
          // Handle both old format (object) and new format (just ID number)
          const teamId = typeof teamData === 'number' ? teamData : (teamData.i !== undefined ? teamData.i : null);
          if (teamId === null || teamId === undefined) return null;
          return {
            id: teamId,
            name: nameMap.get(teamId) || '',
            power: powerMap.get(teamId) || 0,
            gifts: giftsMap.get(teamId) || 0
          };
        });
      }
    }

    // Reconstruct winners
    const winners = state.w || [{}, {}, {}, {}];
    // Ensure winners array has 4 elements
    while (winners.length < 4) {
      winners.push({});
    }

    // Validate the deserialized state
    if (!Array.isArray(rounds) || rounds.length !== 4) {
      throw new Error('Invalid rounds array');
    }
    if (!Array.isArray(winners) || winners.length !== 4) {
      throw new Error('Invalid winners array');
    }

    // Reconstruct locked rounds from count (0-4)
    // Default to [true, false, false, false] if not present
    let lockedRounds = [true, false, false, false];
    if (state.l !== undefined && typeof state.l === 'number') {
      const lockedCount = Math.max(0, Math.min(4, state.l)); // Clamp to 0-4
      lockedRounds = Array(4).fill(false).map((_, index) => index < lockedCount);
    }

    return {
      rounds,
      winners,
      selectedTeamId: state.s !== undefined ? state.s : null,
      lockedRounds
    };
  } catch (error) {
    console.error('Error deserializing game state:', error);
    throw new Error(`Invalid game state format: ${error.message}`);
  }
};

